/**
 * Integration Management API Service
 * Handles all integration framework API calls
 */

import axios from 'axios';
import { config } from '@/src/config';

const API_URL = config.weaveinApi.baseUrl;

// Type definitions
export type IntegrationType = 'health_plan' | 'fhir' | 'crm' | 'housing' | 'benefit_systems' | 'csv_excel';
export type ExecutionMode = 'manual' | 'scheduled' | 'triggered';
export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'partial' | 'cancelled';
export type LogLevel = 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
export type AgentType = 'extractor_agent' | 'transformer_agent' | 'validator_agent' | 'loader_agent' | 'coordinator_agent';
export type TaskStatus = 'pending' | 'running' | 'completed' | 'failed' | 'retry' | 'cancelled';

export interface IntegrationConfig {
  id?: number;
  cbo_id?: number;
  name: string;
  description?: string;
  integration_type: IntegrationType;
  adapter_type: string;
  is_active: boolean;
  connection_config: Record<string, any>;
  credentials_id?: string;
  schedule_config?: {
    frequency: 'hourly' | 'daily' | 'weekly' | 'monthly';
    time?: string;
    timezone?: string;
    enabled?: boolean;
  };
  field_mappings: Record<string, string>;
  transformation_rules?: Record<string, any>;
  validation_rules?: {
    required_fields?: string[];
    duplicate_check?: boolean;
    duplicate_check_field?: string;
    data_quality_score_min?: number;
  };
  error_handling_config?: {
    on_error: 'skip_row' | 'stop_execution' | 'log_and_continue';
    notify?: string[];
    max_retries?: number;
    retry_delay_seconds?: number;
  };
  agent_config?: {
    max_concurrent_tasks?: number;
    batch_size?: number;
    timeout_seconds?: number;
  };
  flow_direction?: 'inbound' | 'outbound' | 'bidirectional';
  sync_options?: string[];
  last_execution_at?: string;
  next_execution_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface IntegrationExecution {
  id: number;
  integration_config_id: number;
  cbo_id: number;
  execution_mode: ExecutionMode;
  execution_status: ExecutionStatus;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  records_fetched: number;
  records_processed: number;
  records_inserted: number;
  records_updated: number;
  records_failed: number;
  records_skipped: number;
  error_count: number;
  error_message?: string;
  execution_plan?: ExecutionPlan;
  result_summary?: {
    success_rate?: number;
    data_quality_score?: number;
    warnings?: string[];
  };
}

export interface ExecutionPlan {
  plan_id: string;
  integration_config_id: number;
  cbo_id: number;
  status: string;
  tasks: AgentTask[];
}

export interface AgentTask {
  task_id: string;
  task_type: string;
  task_name: string;
  agent_type: AgentType;
  status: TaskStatus;
  priority: number;
  depends_on: string[];
  retry_count: number;
  error_message?: string;
  input_data?: Record<string, any>;
  output_data?: Record<string, any>;
}

export interface IntegrationLog {
  id: number;
  integration_execution_id: number;
  log_level: LogLevel;
  log_type?: string;
  message: string;
  details?: Record<string, any>;
  record_id?: string;
  internal_id?: number;
  step_name?: string;
  error_code?: string;
  stack_trace?: string;
  logged_at: string;
}

export interface IntegrationMetrics {
  integration_config_id: number;
  total_executions: number;
  successful_executions: number;
  failed_executions: number;
  total_records_processed: number;
  total_records_inserted: number;
  total_records_updated: number;
  total_records_failed: number;
  avg_duration_seconds: number;
  avg_data_quality_score: number;
  last_7_days: {
    date: string;
    executions: number;
    records: number;
    success_rate: number;
  }[];
}

// Helper to get auth headers
const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id') || '1';
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-CBO-ID': cboId
  };
};

/**
 * Integration Configuration APIs
 */

export const createIntegration = async (config: IntegrationConfig): Promise<IntegrationConfig> => {
  const response = await axios.post(`${API_URL}/api/integrations`, config, {
    headers: getHeaders()
  });
  return response.data;
};

export const listIntegrations = async (filters?: {
  integration_type?: IntegrationType;
  is_active?: boolean;
}): Promise<IntegrationConfig[]> => {
  const response = await axios.get(`${API_URL}/api/integrations`, {
    headers: getHeaders(),
    params: filters
  });
  return response.data;
};

export const getIntegration = async (id: number): Promise<IntegrationConfig> => {
  const response = await axios.get(`${API_URL}/api/integrations/${id}`, {
    headers: getHeaders()
  });
  return response.data;
};

export const updateIntegration = async (
  id: number,
  updates: Partial<IntegrationConfig>
): Promise<{ message: string; id: number }> => {
  const response = await axios.put(`${API_URL}/api/integrations/${id}`, updates, {
    headers: getHeaders()
  });
  return response.data;
};

export const deleteIntegration = async (id: number): Promise<{ message: string }> => {
  const response = await axios.delete(`${API_URL}/api/integrations/${id}`, {
    headers: getHeaders()
  });
  return response.data;
};

/**
 * Integration Execution APIs
 */

export const testConnection = async (id: number): Promise<{
  success: boolean;
  message: string;
  timestamp: string;
}> => {
  const response = await axios.get(`${API_URL}/api/integrations/${id}/test-connection`, {
    headers: getHeaders()
  });
  return response.data;
};

export const executeIntegration = async (
  id: number,
  params?: {
    execution_mode?: ExecutionMode;
    execution_params?: Record<string, any>;
  }
): Promise<{
  execution_id: number;
  status: string;
  plan: ExecutionPlan;
}> => {
  const response = await axios.post(`${API_URL}/api/integrations/${id}/execute`, params || {}, {
    headers: getHeaders()
  });
  return response.data;
};

export const listExecutions = async (
  integrationId: number,
  limit: number = 50
): Promise<IntegrationExecution[]> => {
  const response = await axios.get(`${API_URL}/api/integrations/${integrationId}/executions`, {
    headers: getHeaders(),
    params: { limit }
  });
  return response.data;
};

export const getExecutionLogs = async (
  integrationId: number,
  executionId: number,
  filters?: {
    log_level?: LogLevel;
    limit?: number;
  }
): Promise<IntegrationLog[]> => {
  const response = await axios.get(
    `${API_URL}/api/integrations/${integrationId}/executions/${executionId}/logs`,
    {
      headers: getHeaders(),
      params: filters
    }
  );
  return response.data;
};

/**
 * Metrics APIs
 */

export const getIntegrationMetrics = async (
  integrationId: number,
  dateRange?: { start_date: string; end_date: string }
): Promise<IntegrationMetrics> => {
  const response = await axios.get(`${API_URL}/api/integrations/${integrationId}/metrics`, {
    headers: getHeaders(),
    params: dateRange
  });
  return response.data;
};

/**
 * Helper functions
 */

export const getIntegrationTypeLabel = (type: IntegrationType): string => {
  const labels: Record<IntegrationType, string> = {
    health_plan: 'Health Plan',
    fhir: 'FHIR / EMR',
    crm: 'CRM System',
    housing: 'Housing System',
    benefit_systems: 'HRIS / Benefit Systems',
    // Was missing, so a csv_excel integration rendered a blank type label —
    // labels[type] returned undefined and the map's Record<IntegrationType, …>
    // annotation had been failing the type-check unnoticed.
    csv_excel: 'CSV / Excel'
  };
  return labels[type];
};

export const getStatusColor = (status: ExecutionStatus): string => {
  const colors: Record<ExecutionStatus, string> = {
    pending: 'text-gray-600 bg-gray-100',
    running: 'text-blue-600 bg-blue-100',
    completed: 'text-green-600 bg-green-100',
    failed: 'text-red-600 bg-red-100',
    partial: 'text-yellow-600 bg-yellow-100',
    cancelled: 'text-gray-600 bg-gray-200'
  };
  return colors[status];
};

export const getLogLevelColor = (level: LogLevel): string => {
  const colors: Record<LogLevel, string> = {
    DEBUG: 'text-gray-600 bg-gray-100',
    INFO: 'text-blue-600 bg-blue-100',
    WARNING: 'text-yellow-600 bg-yellow-100',
    ERROR: 'text-red-600 bg-red-100',
    CRITICAL: 'text-red-700 bg-red-200'
  };
  return colors[level];
};

export const formatDuration = (seconds?: number): string => {
  if (!seconds) return 'N/A';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
};

export const calculateSuccessRate = (execution: IntegrationExecution): number => {
  const total = execution.records_processed;
  if (total === 0) return 0;

  const successful = execution.records_inserted + execution.records_updated;
  return Math.round((successful / total) * 100);
};

// ============================================================================
// Integration Workbench
// ============================================================================
// There is deliberately no workbench client here. The Bulk Import Workbench
// calls the backend directly from IntegrationWorkbenchSimple.tsx, against
// /api/workbench/integrations/* — note the order of those two segments.
//
// This file used to export analyzeFileForWorkbench / previewImport /
// executeImport / downloadTemplate / downloadTemplateFile. Nothing ever imported
// them, and three of the five addressed /api/integrations/workbench/*, which the
// backend does not serve and never has. They were removed rather than corrected:
// a plausible-looking API client that 404s is worse than no client at all,
// because the next person to wire a workbench feature reaches for it first.

// ============================================================================
// DOL Form 5500 FOIA Dataset API
// ============================================================================

export interface FOIADatasetYear {
  year: number;
  cached: boolean;
  file_size_bytes: number | null;
  record_count: number | null;
  downloaded_at: string | null;
}

export interface FOIADatasetListResponse {
  years: FOIADatasetYear[];
  cache_dir: string;
}

export interface FOIADownloadRequest {
  years: number[];
  force?: boolean;
}

export interface FOIADownloadStatus {
  year: number;
  status: 'downloaded' | 'downloading' | 'failed' | 'already_cached';
  message: string | null;
  file_size_bytes: number | null;
}

export interface FOIADownloadResponse {
  results: FOIADownloadStatus[];
}

export interface FOIASearchFilters {
  years: number[];
  states?: string[] | null;
  min_participants?: number | null;
  max_participants?: number | null;
  self_insured_only?: boolean;
  min_self_insured_confidence?: number;
  company_name_search?: string | null;
  ein_search?: string | null;
  exclude_small_plans?: boolean;
  limit?: number;
}

export interface FOIASearchResult {
  company_name: string | null;
  ein: string | null;
  city: string | null;
  state: string | null;
  participant_count: number | null;
  is_self_insured: boolean;
  self_insured_confidence: number;
  self_insured_indicators: string[];
  plan_name: string | null;
  plan_year: string | null;
  ack_id: string | null;
}

export interface FOIASearchResponse {
  total_results: number;
  results: FOIASearchResult[];
  years_searched: number[];
  filters_applied: Record<string, any>;
}

export interface FOIAImportRequest {
  years: number[];
  filters: FOIASearchFilters;
  target_table?: 'employers' | 'lb_company_matches';
  auto_approve_threshold?: number;
}

export interface FOIAImportResponse {
  job_id: number;
  status: string;
  total_records: number;
  self_insured_detected: number;
  records_created: number;
  records_updated: number;
  records_skipped: number;
  records_failed: number;
  stages: Record<string, any>;
  duration_seconds: number | null;
}

/**
 * List available DOL FOIA dataset years with cache status
 */
export const getFOIADatasets = async (): Promise<FOIADatasetListResponse> => {
  const response = await axios.get(`${API_URL}/api/dol-5500/datasets`, {
    headers: getHeaders()
  });
  return response.data;
};

/**
 * Download DOL FOIA datasets for specified plan years
 */
export const downloadFOIADatasets = async (request: FOIADownloadRequest): Promise<FOIADownloadResponse> => {
  const response = await axios.post(`${API_URL}/api/dol-5500/datasets/download`, request, {
    headers: getHeaders()
  });
  return response.data;
};

/**
 * Get download/cache status for a specific year
 */
export const getDatasetStatus = async (year: number): Promise<FOIADatasetYear & { file_path?: string }> => {
  const response = await axios.get(`${API_URL}/api/dol-5500/datasets/${year}/status`, {
    headers: getHeaders()
  });
  return response.data;
};

/**
 * Search & filter across cached DOL datasets
 */
export const searchFOIAData = async (filters: FOIASearchFilters): Promise<FOIASearchResponse> => {
  const response = await axios.post(`${API_URL}/api/dol-5500/datasets/search`, filters, {
    headers: getHeaders()
  });
  return response.data;
};

/**
 * Import filtered DOL 5500 records through the ETL pipeline
 */
export const importFOIAData = async (request: FOIAImportRequest): Promise<FOIAImportResponse> => {
  const response = await axios.post(`${API_URL}/api/dol-5500/datasets/import`, request, {
    headers: getHeaders()
  });
  return response.data;
};
